import { animated } from '@react-spring/web'
import cx from 'classnames'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { clamp, isNumeric } from '../../lib/helpers'
import { useBlinkingState } from '../../lib/preview-animation-hook'
import {
  type FrameBuilderStandardSize,
  type FrameBuilderStandardSizeFramePriceReference,
} from '../../lib/state'
import { type StepDefinitionProps } from '../../lib/step-definition'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import { FrameBuilderSelectionContext } from '../../contexts/selection-context'
import { FrameBuilderSelectedStateContext } from '../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../contexts/validation-context'

import Button from '../utility/button'
import PortableText from '../utility/portable-text'
import ConfigurationDefinition from './configuration-definition'

const ArtworkSizeDefinition = ({
  number,
  onClose,
  className,
}: StepDefinitionProps) => {
  const configuration = useContext(FrameBuilderConfigurationContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { selectedStateSelectionMessages } = useContext(
    FrameBuilderSelectionContext,
  )
  const { validationMessages } = useContext(FrameBuilderValidationContext)

  const [width, setWidth] = useState<string>(`${selectedState.artwork.width}`)
  const [height, setHeight] = useState<string>(
    `${selectedState.artwork.height}`,
  )
  const [selectedStandardSizeId, setSelectedStandardSizeId] = useState<
    string | null
  >(null)

  const [sizeOpacity, setIsSizeBlinking] = useBlinkingState()

  const normalizeSize = useMemo(
    () =>
      (size: string, defaultValue: number): string =>
        `${
          isNumeric(size)
            ? clamp(
                parseFloat(size),
                configuration.limits.minimumSize,
                Math.max(
                  configuration.limits.maximumWidth,
                  configuration.limits.maximumHeight,
                ),
              )
            : defaultValue
        }`,
    [
      configuration.limits.maximumWidth,
      configuration.limits.maximumHeight,
      configuration.limits.minimumSize,
    ],
  )
  // TODO: Wrong way to check for valid state!
  const isValidSelection = useMemo(
    () => !validationMessages?.artworkSize,
    [validationMessages],
  )

  const updateWidth = useCallback(
    (width: string) => {
      setWidth(width)

      if (isNumeric(width)) {
        dispatchSelectedState({
          type: 'artworkSize',
          width: parseFloat(width),
        })
      }
    },
    [dispatchSelectedState],
  )
  const updateHeight = useCallback(
    (height: string) => {
      setHeight(height)

      if (isNumeric(height)) {
        dispatchSelectedState({
          type: 'artworkSize',
          height: parseFloat(height),
        })
      }
    },
    [dispatchSelectedState],
  )
  const selectStandardSize = useCallback(
    (standardSize: FrameBuilderStandardSize) => {
      updateWidth(`${standardSize.width}`)
      updateHeight(`${standardSize.height}`)

      // Select passepartout option with size 0, if it is not already selected
      const passepartoutSize = selectedState.passepartout.size?.size || 0
      const passepartoutNoSizePreset = configuration.passepartout.sizes.find(
        (passepartoutSize) => passepartoutSize.size === 0,
      )

      if (
        passepartoutSize > 0 &&
        passepartoutNoSizePreset &&
        passepartoutNoSizePreset._id !== selectedState.passepartout.size?._id
      ) {
        dispatchSelectedState({
          type: 'passepartout',
          size: passepartoutNoSizePreset,
        })
      }

      // Select cheapest glass option, if it is not already selected
      const cheapestGlassId = standardSize.customFramePrices
        ?.filter(
          (customFramePrice: FrameBuilderStandardSizeFramePriceReference) =>
            customFramePrice.profile._ref === selectedState.profile?._id &&
            customFramePrice.texture._ref === selectedState.texture?._id,
        )
        ?.sort(
          (
            { price: priceA }: FrameBuilderStandardSizeFramePriceReference,
            { price: priceB }: FrameBuilderStandardSizeFramePriceReference,
          ) => (priceA > priceB ? -1 : priceA < priceB ? 1 : 0),
        )
        ?.pop()?.glass._ref

      if (cheapestGlassId && cheapestGlassId !== selectedState.glass?._id) {
        const cheapestGlass = configuration.glass.find(
          (glassItem) => cheapestGlassId === glassItem._id,
        )

        if (cheapestGlass) {
          dispatchSelectedState({
            type: 'glass',
            glass: cheapestGlass,
          })
        }
      }
    },
    [
      configuration.glass,
      configuration.passepartout.sizes,
      dispatchSelectedState,
      selectedState.glass,
      selectedState.passepartout.size,
      selectedState.profile,
      selectedState.texture,
      updateHeight,
      updateWidth,
    ],
  )

  // Automatically select a standard size preset based on dimensions
  useEffect(() => {
    const standardSize = configuration.standardSizes.find(
      (standardSize) =>
        (selectedState.artwork.width === standardSize.width &&
          selectedState.artwork.height === standardSize.height) ||
        (selectedState.artwork.height === standardSize.width &&
          selectedState.artwork.width === standardSize.height),
    )
    setSelectedStandardSizeId(standardSize?._id ?? null)
  }, [
    configuration.standardSizes,
    selectedState.artwork.height,
    selectedState.artwork.width,
  ])

  const artworkSizeMessage =
    validationMessages?.artworkSize ??
    selectedStateSelectionMessages?.artworkSize

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsSizeBlinking(true)
    }
  }, [isValidSelection, setIsSizeBlinking, onClose])

  return (
    <ConfigurationDefinition
      number={number}
      onClose={onClose}
      title={frameBuilderStrings.artworkSizeStepTitle}
      className={className}
    >
      <div className="flex flex-col px-6 space-y-6">
        <animated.div
          className="flex flex-col space-y-4"
          style={{
            opacity: sizeOpacity,
          }}
        >
          {configuration.standardSizes.length > 0 && (
            <div className="flex items-end space-x-3">
              <div className="flex-1 flex flex-col justify-center">
                {frameBuilderStrings.artworkSizeStandardSizeLabel && (
                  <span>
                    {frameBuilderStrings.artworkSizeStandardSizeLabel}
                  </span>
                )}
                <div className="relative flex w-full mt-4">
                  {configuration.standardSizes.map((standardSize) => (
                    <div
                      key={standardSize._id}
                      onClick={() => selectStandardSize(standardSize)}
                      className={cx(
                        'flex-1 py-[10px] px-[3px] border border-l-0 border-black first:border-l text-center whitespace-nowrap cursor-pointer',
                        {
                          'text-white bg-pageText':
                            standardSize._id === selectedStandardSizeId,
                          'bg-transparent':
                            standardSize._id !== selectedStandardSizeId,
                        },
                      )}
                    >
                      {standardSize.title}
                    </div>
                  ))}
                  {frameBuilderStrings.artworkSizeCustomOptionLabel && (
                    <div
                      className={cx(
                        'flex-1 py-[10px] px-[3px] border border-l-0 border-black first:border-l text-center whitespace-nowrap',
                        {
                          'text-white bg-pageText cursor-pointer':
                            !selectedStandardSizeId,
                          'bg-transparent cursor-default':
                            !!selectedStandardSizeId,
                        },
                      )}
                    >
                      {frameBuilderStrings.artworkSizeCustomOptionLabel}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-end space-x-3">
            <div className="flex-1 flex flex-col justify-center">
              {frameBuilderStrings.artworkSizeWidthLabel && (
                <span>{frameBuilderStrings.artworkSizeWidthLabel}</span>
              )}

              <div className="relative w-full mt-4 ml-0">
                <input
                  type="number"
                  min={configuration.limits.minimumSize}
                  max={Math.max(
                    configuration.limits.maximumWidth,
                    configuration.limits.maximumHeight,
                  )}
                  step={0.1}
                  value={width}
                  onChange={(event) => updateWidth(event.target.value)}
                  onBlur={(event) =>
                    updateWidth(
                      normalizeSize(
                        event.target.value,
                        selectedState.artwork.width,
                      ),
                    )
                  }
                  required
                  className="w-full min-w-[110px] pt-3 pb-2 pl-2 pr-10 border border-gray-400 invalid:border-error transition-colors duration-100"
                />
                <div className="absolute top-[1px] right-[1px] bottom-[1px] w-[42px] border-l border-gray-400 text-center leading-10 bg-gray-100 z-10">
                  cm
                </div>
              </div>
            </div>

            <div className="flex-0 self-end w-3.5 text-center text-xl leading-10 select-none">
              ×
            </div>

            <div className="flex-1 flex flex-col justify-center">
              {frameBuilderStrings.artworkSizeHeightLabel && (
                <span>{frameBuilderStrings.artworkSizeHeightLabel}</span>
              )}

              <div className="relative w-full mt-4 ml-0">
                <input
                  type="number"
                  min={configuration.limits.minimumSize}
                  max={Math.max(
                    configuration.limits.maximumWidth,
                    configuration.limits.maximumHeight,
                  )}
                  step={0.1}
                  value={height}
                  onChange={(event) => updateHeight(event.target.value)}
                  onBlur={(event) =>
                    updateHeight(
                      normalizeSize(
                        event.target.value,
                        selectedState.artwork.height,
                      ),
                    )
                  }
                  required
                  className="w-full min-w-[110px] pt-3 pb-2 pl-2 pr-10 border border-gray-400 transition-colors duration-100"
                />
                <div className="absolute top-[1px] right-[1px] bottom-[1px] w-[42px] border-l border-gray-400 text-center leading-10 bg-gray-100 z-10">
                  cm
                </div>
              </div>
            </div>
          </div>

          {frameBuilderStrings.artworkSizeWidthAndHeightDescription && (
            <div>
              <PortableText
                content={
                  frameBuilderStrings.artworkSizeWidthAndHeightDescription
                }
              />
            </div>
          )}
        </animated.div>

        {artworkSizeMessage && (
          <p className="uppercase text-gray-500 leading-5">
            {artworkSizeMessage}
          </p>
        )}

        <div className="flex justify-between mt-6">
          <Button
            size="small"
            isVisuallyDisabled={!isValidSelection}
            onClick={handleOkClick}
            className="w-[49%] grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>

      {frameBuilderStrings.artworkSizeStepFooter && (
        <>
          <hr className="bg-gray-300" />
          <div className="flex flex-col px-6 space-y-6">
            <PortableText content={frameBuilderStrings.artworkSizeStepFooter} />
          </div>
        </>
      )}
    </ConfigurationDefinition>
  )
}

export default ArtworkSizeDefinition
